// react
import { useEffect, useRef, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import Autocomplete from '@mui/material/Autocomplete';
import type { AutocompleteProps } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import type { TextFieldProps } from '@mui/material/TextField';

// Address lookup/matching (fuzzy suggestions as the user types) is delegated to the
// official "Adressevælger" search API via a generated RTK Query hook - see
// adressevaelger.openapi.json/src/api/generated/configs/rtk-openapi.adressevaelger.config.cjs for details. Once
// the user picks a concrete address suggestion, we fall back to the existing DAR/MAT
// GraphQL pipeline below to resolve the full location details.
import {
  type AdresseSuggestion,
  useSearchAdresserQuery,
} from '@/api/generated/adressevaelger/endpoints/adressevaelger';
import {
  type DarAdresseByIdQuery,
  type DarHusnummerByIdQuery,
  useLazyDarAdresseByIdQuery,
  useLazyDarAdressepunktByIdQuery,
  useLazyDarHusnummerByIdQuery,
  useLazyDarPostnummerByIdQuery,
} from '@/api/generated/dar/darGraphqlApi';
import {
  useLazyMatCentroideByPointQuery,
  useLazyMatEjerlavByIdQuery,
  useLazyMatJordstykkeByIdQuery,
} from '@/api/generated/mat/matGraphqlApi';

// shared components
import RHFFieldLabel from '@/components/ui/form/RHFFieldLabel';

// relative
import { ADRESSEVAELGER_REFINEMENT_TYPES } from './adressevaelger/refinementTypes';

import proj4 from 'proj4';

export type DarAdresseNode = NonNullable<
  NonNullable<DarAdresseByIdQuery['DAR_Adresse']>['nodes']
>[number];

export interface DanishAddressRegisterSelection {
  node: DarAdresseNode;
  parsed: {
    tekst: string;
    vejnavn: string;
    husnr: string;
    postnr: string;
    postnrnavn: string;
  };
  details: {
    id_lokalId: string;
    municipalityCode: string;
    zipCode: string;
    zipCodeName: string;
    houseNumber: string;
    jordstykkeId: string;
    matrikelnummer: string;
    ejerlavLokalId: string;
    ejerlav?: {
      lokalId: string;
      kode: number | null;
      navn: string;
    };
    coordinates?: {
      x: number;
      y: number;
      lon: number;
      lat: number;
      wkt: string;
      crs: number | null;
    };
  };
}

interface DanishAddressRegisterAutocompleteProps {
  value?: string;
  minSearchChars?: number;
  maxResults?: number;
  onAddressSelect?: (selection: DanishAddressRegisterSelection | null) => void;
  onRefinementChange?: (isRefining: boolean) => void;
  // Escape hatches so callers reach any native MUI prop this component
  // doesn't otherwise own, instead of every option needing to be hand-added
  // here one at a time (that included `required`/`disabled`/`fullWidth`/
  // `size`/`label`/`placeholder`/`helperText`/`error` in an earlier version -
  // all of those are now just regular `textFieldProps`/`autocompleteProps`
  // entries). Domain-managed props (options, value, onChange, loading,
  // renderInput, ...) are intentionally excluded - they're owned by this
  // component's search/selection logic.
  autocompleteProps?: Partial<
    Omit<
      AutocompleteProps<AdresseSuggestion, false, false, true>,
      | 'options'
      | 'value'
      | 'onChange'
      | 'inputValue'
      | 'onInputChange'
      | 'getOptionLabel'
      | 'isOptionEqualToValue'
      | 'filterOptions'
      | 'loading'
      | 'renderInput'
      | 'open'
      | 'onOpen'
      | 'onClose'
    >
  >;
  textFieldProps?: Partial<TextFieldProps> & { 'data-testid'?: string };
}

interface DanishAddressRegisterAutocompleteInnerProps extends DanishAddressRegisterAutocompleteProps {
  initialValue: string;
}

const ETRS89_UTM32 =
  '+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs +type=crs';
const WGS84 = '+proj=longlat +datum=WGS84 +no_defs +type=crs';

const DAR_HUSNUMMER_POINT_IDS_BY_ID_QUERY = `
  query DarHusnummerPointIdsById(
    $id: String!
    $first: Int = 1
    $registreringstid: DafDateTime!
    $virkningstid: DafDateTime
  ) {
    DAR_Husnummer(
      first: $first
      registreringstid: $registreringstid
      virkningstid: $virkningstid
      where: { id_lokalId: { eq: $id } }
    ) {
      nodes {
        adgangspunkt
        vejpunkt
      }
    }
  }
`;

const parseDarAddressText = (addressText = '', husnr = '') => {
  const [streetWithNumber = '', postalPart = ''] = addressText
    .split(',')
    .map((part) => part.trim());

  const postalMatch = postalPart.match(/^(\d{4})\s*(.*)$/);
  const postnr = postalMatch?.[1] ?? '';
  const postnrnavn = postalMatch?.[2] ?? '';

  const streetNumberMatch = streetWithNumber.match(/^(.*?)(\d+\w*)$/);
  const fallbackHusnr = streetNumberMatch?.[2]?.trim() ?? '';
  const parsedHusnr = husnr || fallbackHusnr;
  const vejnavn = (
    streetNumberMatch?.[1]?.trim() ||
    streetWithNumber.replace(parsedHusnr, '').trim() ||
    streetWithNumber
  ).trim();

  return {
    tekst: addressText,
    vejnavn,
    husnr: parsedHusnr,
    postnr,
    postnrnavn,
  };
};

const parseWktPoint = (wkt?: string | null) => {
  if (!wkt) return null;
  const match = wkt.match(/POINT(?:\s+Z|\s+M|\s+ZM)?\s*\(([^)]+)\)/i);
  if (!match?.[1]) return null;

  const coordinates = match[1].trim().split(/\s+/);
  if (coordinates.length < 2) return null;

  const x = Number(coordinates[0]);
  const y = Number(coordinates[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  return { x, y };
};

const toWgs84 = (x: number, y: number) => {
  try {
    const [lon, lat] = proj4(ETRS89_UTM32, WGS84, [x, y]);
    return {
      lon: Number.isFinite(lon) ? lon : Number.NaN,
      lat: Number.isFinite(lat) ? lat : Number.NaN,
    };
  } catch {
    return { lon: Number.NaN, lat: Number.NaN };
  }
};

const extractMunicipalityCode = (
  ...values: Array<string | number | null | undefined>
) => {
  for (const rawValue of values) {
    if (rawValue == null) continue;

    const normalized = String(rawValue).trim();
    if (!normalized) continue;

    const directCodeMatch = normalized.match(/^(\d{4})$/);
    if (directCodeMatch?.[1]) {
      return directCodeMatch[1];
    }

    const kommunekodeVejkodeMatch = normalized.match(/^(\d{4})[-_].*$/);
    if (kommunekodeVejkodeMatch?.[1]) {
      return kommunekodeVejkodeMatch[1];
    }
  }

  return '';
};

const fetchDarHusnummerPointIdsById = async ({
  id,
  registreringstid,
  virkningstid,
}: {
  id: string;
  registreringstid: string;
  virkningstid?: string;
}) => {
  const response = await fetch('/dar-graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: DAR_HUSNUMMER_POINT_IDS_BY_ID_QUERY,
      variables: {
        id,
        registreringstid,
        virkningstid,
      },
    }),
  });

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json()) as {
    data?: {
      DAR_Husnummer?: {
        nodes?: Array<{
          adgangspunkt?: string | null;
          vejpunkt?: string | null;
        }> | null;
      } | null;
    };
  };

  return payload?.data?.DAR_Husnummer?.nodes?.[0] ?? null;
};

const DanishAddressRegisterAutocompleteInner = ({
  initialValue,
  minSearchChars = 3,
  maxResults = 20,
  onAddressSelect,
  onRefinementChange,
  autocompleteProps,
  textFieldProps,
}: DanishAddressRegisterAutocompleteInnerProps) => {
  const { t } = useTranslation('common');
  const {
    required = true,
    label,
    placeholder,
    helperText,
    error,
    InputProps: textFieldInputProps,
    ...restTextFieldProps
  } = textFieldProps ?? {};
  const [inputValue, setInputValue] = useState(initialValue);
  const [isEnrichingSelection, setIsEnrichingSelection] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(initialValue.trim());
  const [open, setOpen] = useState(false);

  // MUI closes the popup after every selectOption. For an Adressevælger
  // refinement result that selection is only an intermediate search step, so
  // remember it synchronously and ignore that one close event. A ref is used
  // instead of state because onClose runs in the same event cycle as onChange.
  const keepOpenAfterSelectionRef = useRef(false);

  const [fetchDarAdresseById] = useLazyDarAdresseByIdQuery();
  const [fetchDarHusnummer] = useLazyDarHusnummerByIdQuery();
  const [fetchDarAdressepunkt] = useLazyDarAdressepunktByIdQuery();
  const [fetchDarPostnummer] = useLazyDarPostnummerByIdQuery();
  const [fetchMatCentroideByPoint] = useLazyMatCentroideByPointQuery();
  const [fetchMatJordstykke] = useLazyMatJordstykkeByIdQuery();
  const [fetchMatEjerlav] = useLazyMatEjerlavByIdQuery();

  const trimmedSearch = inputValue.trim();

  // External system synchronization: debounce the raw input before it drives the
  // Adressevælger search query below.
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearch(trimmedSearch);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [trimmedSearch]);

  // Fuzzy address suggestion search against the Adressevælger API (proxied through
  // the BFF, which injects the real token server-side - see bff/server.js). Matching/
  // lookup is fully delegated to Adressevælger here; the DAR/MAT GraphQL pipeline
  // below only runs once the user picks a concrete suggestion.
  const {
    data: searchResult,
    isFetching: isSearching,
    isError: hasSearchNetworkError,
  } = useSearchAdresserQuery(
    { tekst: debouncedSearch, token: 'proxied', maksimum: maxResults },
    { skip: debouncedSearch.length < minSearchChars },
  );

  const suggestions = searchResult?.fund ?? [];
  const hasSearchError =
    hasSearchNetworkError || searchResult?.status === 'fejl';

  const selectedOption =
    suggestions.find((option) => option.titel === inputValue) ?? null;

  // Runs the existing DAR/MAT GraphQL enrichment pipeline for a resolved DAR
  // address node, exactly as before, then emits the final selection.
  const enrichAndEmitSelection = async (node: DarAdresseNode) => {
    const registreringstid = new Date().toISOString();

    const parsed = parseDarAddressText(
      node.adressebetegnelse ?? '',
      node.husnummer ?? '',
    );

    let houseNumber = parsed.husnr;
    let zipCode = parsed.postnr;
    let zipCodeName = parsed.postnrnavn;
    let municipalityCode = '';
    let jordstykkeId = '';
    let matrikelnummer = '';
    let ejerlavLokalId = '';
    let ejerlav:
      | {
          lokalId: string;
          kode: number | null;
          navn: string;
        }
      | undefined;
    let coordinates:
      | {
          x: number;
          y: number;
          lon: number;
          lat: number;
          wkt: string;
          crs: number | null;
        }
      | undefined;

    const temporalCandidates = Array.from(
      new Set(
        [registreringstid, node.registreringFra, node.virkningFra].filter(
          (candidate): candidate is string => Boolean(candidate),
        ),
      ),
    );

    const temporalPairs = Array.from(
      new Map(
        temporalCandidates.flatMap((candidate) => [
          [
            `${candidate}|${candidate}`,
            {
              registreringstid: candidate,
              virkningstid: candidate,
            },
          ],
          [
            `${candidate}|${node.virkningFra ?? ''}`,
            {
              registreringstid: candidate,
              virkningstid: node.virkningFra || undefined,
            },
          ],
        ]),
      ).values(),
    );

    try {
      if (node.husnummer) {
        let husnummerNode:
          | NonNullable<
              NonNullable<DarHusnummerByIdQuery['DAR_Husnummer']>['nodes']
            >[number]
          | undefined;
        for (const temporal of temporalPairs) {
          const husnummerResponse = await fetchDarHusnummer(
            {
              id: node.husnummer,
              first: 1,
              registreringstid: temporal.registreringstid,
              virkningstid: temporal.virkningstid,
            },
            false,
          ).unwrap();

          const candidateNode = husnummerResponse?.DAR_Husnummer?.nodes?.[0] as
            | NonNullable<
                NonNullable<DarHusnummerByIdQuery['DAR_Husnummer']>['nodes']
              >[number]
            | undefined;

          if (!candidateNode) continue;
          if (!husnummerNode) {
            husnummerNode = candidateNode;
          }

          if (
            candidateNode.jordstykke ||
            candidateNode.placeretPaaForeloebigtJordstykke
          ) {
            husnummerNode = candidateNode;
            break;
          }
        }

        if (husnummerNode) {
          houseNumber = husnummerNode.husnummertekst ?? houseNumber;
          zipCode = husnummerNode.postnummer ?? zipCode;
          municipalityCode = extractMunicipalityCode(
            husnummerNode.vejmidte,
            husnummerNode.kommuneinddeling,
            municipalityCode,
          );
          jordstykkeId =
            husnummerNode.jordstykke ??
            husnummerNode.placeretPaaForeloebigtJordstykke ??
            '';

          if (husnummerNode.postnummer) {
            const postnummerResponse = await fetchDarPostnummer(
              {
                id: husnummerNode.postnummer,
                first: 1,
                registreringstid,
                virkningstid: node.virkningFra || undefined,
              },
              false,
            ).unwrap();

            const postnummerNode =
              postnummerResponse?.DAR_Postnummer?.nodes?.[0];
            if (postnummerNode) {
              zipCode = postnummerNode.postnr ?? zipCode;
              zipCodeName = postnummerNode.navn ?? zipCodeName;
            }
          }

          let adressepunktId = husnummerNode.adgangspunkt ?? '';

          if (!adressepunktId && node.husnummer) {
            for (const temporal of temporalPairs) {
              const pointIds = await fetchDarHusnummerPointIdsById({
                id: node.husnummer,
                registreringstid: temporal.registreringstid,
                virkningstid: temporal.virkningstid,
              });

              adressepunktId =
                pointIds?.adgangspunkt ?? pointIds?.vejpunkt ?? '';

              if (adressepunktId) {
                break;
              }
            }
          }

          if (adressepunktId) {
            for (const temporal of temporalPairs) {
              const adressepunktResponse = await fetchDarAdressepunkt(
                {
                  id: adressepunktId,
                  first: 1,
                  registreringstid: temporal.registreringstid,
                  virkningstid: temporal.virkningstid,
                },
                false,
              ).unwrap();

              const adressepunktNode =
                adressepunktResponse?.DAR_Adressepunkt?.nodes?.[0];
              const wkt = adressepunktNode?.position?.wkt ?? '';
              const parsedWktPoint = parseWktPoint(wkt);

              if (!parsedWktPoint) {
                continue;
              }

              const wgs84 = toWgs84(parsedWktPoint.x, parsedWktPoint.y);
              coordinates = {
                x: parsedWktPoint.x,
                y: parsedWktPoint.y,
                lon: wgs84.lon,
                lat: wgs84.lat,
                wkt,
                crs: adressepunktNode?.position?.crs ?? null,
              };
              break;
            }
          }
        }
      }

      // Fallback: resolve jordstykke via MAT centroide spatial intersection
      // when DAR husnummer has no jordstykke relation.
      if (!jordstykkeId && coordinates?.wkt) {
        const centroideResponse = await fetchMatCentroideByPoint(
          {
            wkt: coordinates.wkt,
            crs: coordinates.crs ?? 25832,
            first: 1,
            registreringstid,
            virkningstid: node.virkningFra || undefined,
          },
          false,
        ).unwrap();

        const centroideNode = centroideResponse?.MAT_Centroide?.nodes?.[0];
        if (centroideNode?.jordstykkeLokalId) {
          jordstykkeId = centroideNode.jordstykkeLokalId;
        }
      }

      if (jordstykkeId) {
        let jordstykkeNode:
          | {
              id_lokalId: string;
              matrikelnummer?: string | null;
              ejerlavLokalId?: string | null;
            }
          | undefined;

        for (const temporal of temporalPairs) {
          const jordstykkeResponse = await fetchMatJordstykke(
            {
              id: jordstykkeId,
              first: 1,
              registreringstid: temporal.registreringstid,
              virkningstid: temporal.virkningstid,
            },
            false,
          ).unwrap();

          const candidateNode = jordstykkeResponse?.MAT_Jordstykke?.nodes?.[0];
          if (!candidateNode) continue;

          if (!jordstykkeNode) {
            jordstykkeNode = candidateNode;
          }

          if (candidateNode.matrikelnummer || candidateNode.ejerlavLokalId) {
            jordstykkeNode = candidateNode;
            break;
          }
        }

        if (jordstykkeNode) {
          jordstykkeId = jordstykkeNode.id_lokalId ?? jordstykkeId;
          matrikelnummer = jordstykkeNode.matrikelnummer ?? '';
          ejerlavLokalId = jordstykkeNode.ejerlavLokalId ?? '';

          if (ejerlavLokalId) {
            const ejerlavResponse = await fetchMatEjerlav(
              {
                id: ejerlavLokalId,
                first: 1,
                registreringstid,
                virkningstid: node.virkningFra || undefined,
              },
              false,
            ).unwrap();

            const ejerlavNode = ejerlavResponse?.MAT_Ejerlav?.nodes?.[0];
            if (ejerlavNode) {
              ejerlav = {
                lokalId: ejerlavNode.id_lokalId,
                kode: ejerlavNode.ejerlavskode ?? null,
                navn: ejerlavNode.ejerlavsnavn ?? '',
              };
            }
          }
        }
      }
    } catch (enrichmentError) {
      console.error('[DanishAddressRegisterAutocomplete] enrich error', {
        node,
        enrichmentError,
      });
    } finally {
      const selection: DanishAddressRegisterSelection = {
        node,
        parsed: {
          ...parsed,
          husnr: houseNumber || parsed.husnr,
          postnr: zipCode || parsed.postnr,
          postnrnavn: zipCodeName || parsed.postnrnavn,
        },
        details: {
          id_lokalId: node.id_lokalId ?? '',
          municipalityCode,
          zipCode: zipCode || '',
          zipCodeName: zipCodeName || '',
          houseNumber: houseNumber || '',
          jordstykkeId,
          matrikelnummer,
          ejerlavLokalId,
          ejerlav,
          coordinates,
        },
      };

      console.log(
        '[DanishAddressRegisterAutocomplete] finished enriched location',
        selection,
      );

      if (!selection.details.coordinates) {
        console.warn(
          '[DanishAddressRegisterAutocomplete] enriched location is missing coordinates',
          selection,
        );
      }

      onAddressSelect?.(selection);
    }
  };

  // Resolves an Adressevælger suggestion to its DAR node by id, then runs the
  // existing GraphQL enrichment pipeline above.
  const resolveAndEnrichSuggestion = async (suggestion: AdresseSuggestion) => {
    setIsEnrichingSelection(true);

    try {
      const adresseResponse = await fetchDarAdresseById(
        { id: suggestion.id, first: 1 },
        false,
      ).unwrap();

      const node = adresseResponse?.DAR_Adresse?.nodes?.[0];

      if (!node?.id_lokalId) {
        console.error(
          '[DanishAddressRegisterAutocomplete] address not found in DAR',
          suggestion,
        );
        onAddressSelect?.(null);
        return;
      }

      await enrichAndEmitSelection(node);
    } catch (resolveError) {
      console.error(
        '[DanishAddressRegisterAutocomplete] failed to resolve DAR address',
        resolveError,
      );
      onAddressSelect?.(null);
    } finally {
      setIsEnrichingSelection(false);
    }
  };

  return (
    <Autocomplete<AdresseSuggestion, false, false, true>
      openOnFocus
      freeSolo
      autoHighlight
      clearOnBlur={false}
      {...autocompleteProps}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={(_event, reason) => {
        if (reason === 'selectOption' && keepOpenAfterSelectionRef.current) {
          keepOpenAfterSelectionRef.current = false;
          setOpen(true);
          return;
        }

        keepOpenAfterSelectionRef.current = false;
        setOpen(false);
      }}
      options={suggestions}
      loading={isSearching || isEnrichingSelection}
      value={selectedOption}
      inputValue={inputValue}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : (option?.titel ?? '')
      }
      isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
      filterOptions={(x) => x}
      loadingText={t('danishAddressAutocomplete.loading')}
      noOptionsText={
        hasSearchError
          ? t('danishAddressAutocomplete.errorLoadingAddresses')
          : trimmedSearch.length < minSearchChars
            ? t('danishAddressAutocomplete.typeAtLeastChars', {
                count: minSearchChars,
              })
            : t('danishAddressAutocomplete.noOptions')
      }
      onInputChange={(_event, newInputValue, reason) => {
        setInputValue(newInputValue ?? '');

        if (reason === 'input' || reason === 'clear') {
          onRefinementChange?.(false);
        }
      }}
      onChange={(_event, selected) => {
        if (!selected || typeof selected === 'string') {
          onRefinementChange?.(false);
          onAddressSelect?.(null);
          return;
        }

        if (ADRESSEVAELGER_REFINEMENT_TYPES.has(selected.type)) {
          // This is an intermediate Adressevælger result (for example a street
          // or access address), not a value the form should receive. Use it as
          // the next search term and keep the popup open so the user can finish
          // selecting the address.
          keepOpenAfterSelectionRef.current = true;
          onRefinementChange?.(true);
          setInputValue(selected.titel);
          setDebouncedSearch(selected.titel.trim());
          setOpen(true);
          return;
        }

        keepOpenAfterSelectionRef.current = false;
        onRefinementChange?.(false);
        void resolveAndEnrichSuggestion(selected);
      }}
      renderInput={(params) => {
        const displayLabel = label ?? t('danishAddressAutocomplete.label');
        const defaultHintText = `${required ? t('danishAddressAutocomplete.fieldRequired') : t('danishAddressAutocomplete.fieldOptional')} ${t('danishAddressAutocomplete.helperText')}`;
        return (
          <Box>
            {displayLabel ? (
              <RHFFieldLabel htmlFor={params.id} required={required}>
                {displayLabel}
              </RHFFieldLabel>
            ) : null}

            <TextField
              {...params}
              {...restTextFieldProps}
              required={required}
              placeholder={
                hasSearchError
                  ? t('danishAddressAutocomplete.errorLoadingAddresses')
                  : (placeholder ?? defaultHintText)
              }
              helperText={
                hasSearchError
                  ? t('danishAddressAutocomplete.errorLoadingAddresses')
                  : (helperText ?? defaultHintText)
              }
              error={hasSearchError || Boolean(error)}
              slotProps={{
                input: {
                  ...params.InputProps,
                  ...textFieldInputProps,
                  endAdornment: (
                    <>
                      {isSearching || isEnrichingSelection ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
                htmlInput: {
                  ...params.inputProps,
                },
              }}
            />
          </Box>
        );
      }}
    />
  );
};

const DanishAddressRegisterAutocomplete = (
  props: DanishAddressRegisterAutocompleteProps,
) => {
  const { value = '', onRefinementChange } = props;

  useEffect(() => {
    onRefinementChange?.(false);
  }, [value, onRefinementChange]);

  return (
    <DanishAddressRegisterAutocompleteInner
      key={value}
      initialValue={value}
      {...props}
    />
  );
};

export default DanishAddressRegisterAutocomplete;
